import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import {
  find,
  filter,
  get,
  isEmpty,
  isNumber,
  isString,
  union,
  without,
  isMatch,
  kebabCase,
  split,
  cloneDeep,
  eq,
  startCase,
  isFunction,
  isArray,
  chain,
  toLower,
  has,
  isBoolean,
  isPlainObject,
} from 'lodash';

import { Observable, observableThrowError, rx } from '@shared/rx';
import {
  PermissionType,
  SupportedAppCodes,
  IUserPermission,
  ENTITY_TYPES,
  IMenuItem,
  IEntityConfig,
  PermCustomCheckFunction,
  CpxUserPermission,
  IPermissionCheckResult,
  IUserPermBuilder,
} from '@shared/types';
import { environment } from '../../../../environments/environment';
import { PERMISSION_TYPES, APP_LEVEL_PERMISSIONS } from '@config/index';

import { StateService } from '../state-service/state.service';
import { EntityServiceProvider } from '../datasource';

interface IPermissionServiceConfig {
  basePermissionURL: string;
  requiredAppCodes: string[];
  disablePermissionCheck: boolean;
  alwaysPermittedDependencies: string[];
  alwaysPermittedPermissions?: string[];
}

/**
 * check is IPermissionCheckResult
 *
 * @param value target data to check
 * @returns is pass
 */
const isPermissionCheckResult = (
  value: unknown,
): value is IPermissionCheckResult => {
  return (
    isPlainObject(value) &&
    has(value, 'hasPermission') &&
    isBoolean(get(value, 'hasPermission')) &&
    has(value, 'name')
  );
};

const defaultPermissionSearchCriteria: Partial<IUserPermission> = {
  app_code: SupportedAppCodes.XVI,
  type: PermissionType.action,
};

/**
 * # PermissionService
 * Defines the service for checking user permissions
 *
 * @module client/src/app/shared/services
 */
@Injectable()
export class PermissionService {
  public static config: IPermissionServiceConfig = {
    basePermissionURL: '/me/permissions/',
    requiredAppCodes: [SupportedAppCodes.XVI],
    disablePermissionCheck:
      String(environment['disablePermissionCheck']) === 'true',
    alwaysPermittedDependencies: [],
    alwaysPermittedPermissions: [],
  };

  public static composeAddPermissionName(entityType): string {
    return `${entityType}.${PERMISSION_TYPES.Add}`;
  }

  public static composeDeletePermissionName(entityType): string {
    return `${entityType}.${PERMISSION_TYPES.Delete}`;
  }

  public static composeManagePermissionName(
    entityType,
    entityPrefix: string = '',
  ): string {
    return isEmpty(entityPrefix)
      ? `${entityType}.${PERMISSION_TYPES.Manage}`
      : `${entityType}.${entityPrefix}.${PERMISSION_TYPES.Manage}`;
  }

  public static composeViewPermissionName(
    entityType: string,
    options?: {
      appCode?: SupportedAppCodes;
      camelCase?: boolean;
    },
  ): string {
    const { appCode, camelCase } = options || {};

    const formattedEntityType = startCase(entityType).replace(/\s/g, '');

    const permissionName = `${camelCase ? formattedEntityType : entityType}.${PERMISSION_TYPES.View}`;

    if (isEmpty(entityType)) {
      return permissionName;
    }

    const prefixMapping: Record<string, string> = {
      [SupportedAppCodes.XVI]: 'VenuesInventory',
    };

    const prefix = get(prefixMapping, appCode, '');

    return isEmpty(prefix) ? permissionName : `${prefix}.${permissionName}`;
  }

  public static composeOperatePermissionName(
    entityType: ENTITY_TYPES,
    code: string,
  ): string {
    return `${entityType}.${code}`;
  }

  public static getRequiredPermissions(
    entityConfig: any,
    path: string,
  ): string[] {
    const requiredEntities: string[] = union(
      get(entityConfig, [path, 'documentQuery', 'include_entities'], []),
      get(entityConfig, [path, 'requiredMasterLists'], []).map((param) =>
        isString(param) ? param : param.entityType,
      ),
    );

    return without(
      requiredEntities,
      ...PermissionService.config.alwaysPermittedDependencies,
    ).map((entityType) => `${entityType}.View`);
  }

  public static getRequiredPermissionsForList(entityConfig: any): string[] {
    return PermissionService.getRequiredPermissions(entityConfig, 'list');
  }

  public static checkPermissionName(permission, fullSearchCriteria): boolean {
    const clonePermission = cloneDeep(permission);
    const splitPermissionName = split(clonePermission.name, '.');
    if (
      permission.app_code !== SupportedAppCodes.XVI ||
      splitPermissionName.length < 3
    ) {
      return (
        eq(get(permission, 'app_code'), get(fullSearchCriteria, 'app_code')) &&
        eq(get(permission, 'name'), get(fullSearchCriteria, 'name'))
      );
    }

    splitPermissionName.splice(0, 1);
    splitPermissionName[0] = kebabCase(splitPermissionName[0]);
    clonePermission.name = splitPermissionName.join('.');

    return isMatch(clonePermission, fullSearchCriteria);
  }

  /**
   * Is not intended to be used outside the service.
   * Consider moving to a simple helper function to avoid exposure
   */
  public static getPermissionValue(
    permissions: IUserPermission[],
    searchCriteria: Partial<IUserPermission> | string,
    appCode?: SupportedAppCodes,
  ): boolean | number {
    const partialSearchCriteria = isString(searchCriteria)
      ? { name: searchCriteria }
      : searchCriteria;
    const fullSearchCriteria = Object.assign(
      {},
      defaultPermissionSearchCriteria,
      appCode ? { app_code: appCode } : {},
      partialSearchCriteria,
    );

    return (
      PermissionService.config.disablePermissionCheck ||
      // eslint-disable-next-line
      this.isAlwaysPermittedPermission(fullSearchCriteria) ||
      get(
        // eslint-disable-next-line
        find(permissions, (permission) =>
          this.checkPermissionName(permission, fullSearchCriteria),
        ),
        'value',
        false,
      )
    );
  }

  public static checkPermission(
    permissions: any[],
    requiredPermission: CpxUserPermission,
  ): IPermissionCheckResult | IPermissionCheckResult[] {
    const isStrPermission = isString(requiredPermission);
    const isFunctionPermission =
      !isStrPermission && isFunction(requiredPermission);

    if (isFunctionPermission) {
      const permissionResOrData = (requiredPermission as IUserPermBuilder)(
        permissions,
      );

      if (isPermissionCheckResult(permissionResOrData)) {
        return {
          app_code: null,
          ...permissionResOrData,
        };
      }

      return PermissionService.checkPermissions(
        permissions,
        ...((isArray(permissionResOrData)
          ? permissionResOrData
          : [permissionResOrData]) as CpxUserPermission[]),
      );
    }

    const name = isStrPermission
      ? requiredPermission
      : requiredPermission['name'];
    const app_code = isStrPermission ? null : requiredPermission['app_code'];

    const targetPerm = {
      name,
      app_code,
      type: get(requiredPermission, 'type', PermissionType.action),
      value: get(requiredPermission, 'value'),
    };

    const defaultHasPermissionCheck = (
      perm: Omit<IUserPermission, 'customHasPermissionCheck'>,
      userPermissions: IUserPermission[],
    ) =>
      !!PermissionService.getPermissionValue(
        userPermissions,
        perm.name,
        perm.app_code,
      );

    const customHasPermissionCheck =
      !isStrPermission && get(requiredPermission, 'customHasPermissionCheck');

    const hasPermissionCheck: PermCustomCheckFunction = isFunction(
      customHasPermissionCheck,
    )
      ? customHasPermissionCheck
      : defaultHasPermissionCheck;

    return {
      ...targetPerm,
      hasPermission: hasPermissionCheck(targetPerm, permissions),
    };
  }

  public static checkPermissions(
    permissions: any[],
    ...requiredPermissions: CpxUserPermission[]
  ): IPermissionCheckResult[] {
    return chain(requiredPermissions)
      .flatMap((permission) => {
        const permissionCheckResult = PermissionService.checkPermission(
          permissions,
          permission,
        );

        return isArray(permissionCheckResult)
          ? permissionCheckResult
          : [permissionCheckResult];
      })
      .uniqBy(
        (permission) =>
          `${toLower(permission.app_code)}-${toLower(permission.name)}`,
      )
      .value();
  }

  public static hasPermissions(
    permissions: any[],
    ...requiredPermissions: IMenuItem['requiredPermissions']
  ): boolean {
    return requiredPermissions.every((permission) => {
      const permissionCheckResult = PermissionService.checkPermission(
        permissions,
        permission,
      );

      return isArray(permissionCheckResult)
        ? permissionCheckResult.every((result) => result.hasPermission)
        : permissionCheckResult.hasPermission;
    });
  }

  public static hasAddPermission(
    permissions: any[],
    entityType: string,
  ): boolean {
    const name = PermissionService.composeAddPermissionName(entityType);

    return !!PermissionService.getPermissionValue(permissions, name);
  }

  public static hasManagePermission(
    permissions: any[],
    entityType: string,
    entityPrefix: string = '',
    appCode: SupportedAppCodes = SupportedAppCodes.XVI,
  ): boolean {
    const name = PermissionService.composeManagePermissionName(
      entityType,
      entityPrefix,
    );

    return !!PermissionService.getPermissionValue(permissions, name, appCode);
  }

  public static hasViewPermission(
    permissions: any[],
    entityType: string,
    appCode: SupportedAppCodes = SupportedAppCodes.XVI,
  ): boolean {
    const name = PermissionService.composeViewPermissionName(entityType);

    return !!PermissionService.getPermissionValue(permissions, name, appCode);
  }

  public static hasViewDeletedPermission(permissions: any[]): boolean {
    const name = 'deleted-entities.View';

    return !!PermissionService.getPermissionValue(permissions, name);
  }

  public static hasDeletePermission(
    permissions: any[],
    entityType: string,
  ): boolean {
    const name = PermissionService.composeDeletePermissionName(entityType);

    return !!PermissionService.getPermissionValue(permissions, name);
  }

  public static hasOperatePermissionByCode(
    permissions: IUserPermission[],
    entityType: ENTITY_TYPES,
    code: string,
  ): boolean {
    const name = PermissionService.composeOperatePermissionName(
      entityType,
      code,
    );

    return !!PermissionService.getPermissionValue(permissions, name);
  }

  public static isAlwaysPermittedPermission(permission): boolean {
    const permissionName = permission.name;
    const permissionEntityType = permissionName.replace(
      /.View|.Add|.Manage|.Delete/gi,
      '',
    );

    return PermissionService.config.alwaysPermittedPermissions.includes(
      permissionEntityType,
    );
  }

  public static checkPermissionsDetailed(
    permissions: any[],
    ...requiredPermissions: IMenuItem['requiredPermissions']
  ): IPermissionCheckResult[] {
    return PermissionService.checkPermissions(
      permissions,
      ...requiredPermissions,
    );
  }

  private get baseUrl(): string {
    return environment.xprtBackendURI;
  }

  public constructor(
    private stateService: StateService,
    private http: HttpClient,
    private entityServiceProvider: EntityServiceProvider,
  ) {}

  /**
   * Searches for service by uiRoute
   * Uses its entity type to compose defult View permission
   */
  public getDefaultRoutePermissions(uiRoute: string): string[] {
    const serviceClass = this.entityServiceProvider.getServiceClass({
      config: { uiRoute },
    });
    const config: IEntityConfig = get(serviceClass, 'config', null);

    // use entityNameToCheckPermissions if available or ENTITY_TYPE otherwise
    const entityType =
      get(config, 'entityNameToCheckPermissions') ?? get(config, 'ENTITY_TYPE');

    const currentEntityPermissions: string[] = serviceClass
      ? [PermissionService.composeViewPermissionName(entityType)]
      : [];

    return currentEntityPermissions.concat(
      PermissionService.getRequiredPermissionsForList(config),
    );
  }

  public fetchUserPermissions(): Observable<any> {
    const composedPermissionUrl = `${this.baseUrl}${PermissionService.config.basePermissionURL}`;

    return this.http
      .get(composedPermissionUrl)
      .pipe(
        rx.catchError(({ error }: HttpErrorResponse) =>
          observableThrowError(get(error, 'errors')),
        ),
      );
  }

  public getUserPermissions(): Promise<any[]> {
    if (PermissionService.config.disablePermissionCheck) {
      return Promise.resolve([]);
    }

    return this.stateService
      .select(['userSession', 'userPermissions'])
      .pipe(
        rx.find((userPermissions) => isNumber(get(userPermissions, 'total'))),
        rx.map((userPermissions) => get(userPermissions, 'items', [])),
      )
      .toPromise();
  }

  public canAdd(entityType: string): boolean {
    return !!PermissionService.hasAddPermission(
      this.getUserPermissionsSync(),
      entityType,
    );
  }

  public canDelete(entityType: string): boolean {
    return !!PermissionService.hasDeletePermission(
      this.getUserPermissionsSync(),
      entityType,
    );
  }

  public canManage(
    entityType: string,
    entityPrefix: string = '',
    appCode: SupportedAppCodes = SupportedAppCodes.XVI,
  ): boolean {
    return !!PermissionService.hasManagePermission(
      this.getUserPermissionsSync(),
      entityType,
      entityPrefix,
      appCode,
    );
  }

  public canView(
    entityType: string,
    appCode: SupportedAppCodes = SupportedAppCodes.XVI,
  ): boolean {
    return !!PermissionService.hasViewPermission(
      this.getUserPermissionsSync(),
      entityType,
      appCode,
    );
  }

  public canViewDeleted(): boolean {
    return !!PermissionService.hasViewDeletedPermission(
      this.getUserPermissionsSync(),
    );
  }

  public canOperateByCode(entityType: ENTITY_TYPES, code: string): boolean {
    return !!PermissionService.hasOperatePermissionByCode(
      this.getUserPermissionsSync(),
      entityType,
      code,
    );
  }

  public hasPermissions(...requiredPermissions: any[]): boolean {
    return PermissionService.hasPermissions(
      this.getUserPermissionsSync(),
      ...requiredPermissions,
    );
  }

  public hasPortalPermissions(...requiredPermissions: any[]): boolean {
    const portalPermission = this.getUserPortalPermissionsSync();

    return requiredPermissions.every(
      (name) =>
        !!PermissionService.getPermissionValue(portalPermission, {
          name,
          app_code: SupportedAppCodes.XPRT,
        }),
    );
  }

  public hasXDMPermissions(...requiredPermissions: any[]): boolean {
    const xdmPermission = this.getUserXDMPermissionsSync();

    return requiredPermissions.every(
      (name) =>
        !!PermissionService.getPermissionValue(xdmPermission, {
          name,
          app_code: SupportedAppCodes.XDM,
        }),
    );
  }

  public isRoutePermitted(
    uiRoute: string,
    permissions: IMenuItem['requiredPermissions'] = this.getDefaultRoutePermissions(
      uiRoute,
    ),
  ): boolean {
    return this.hasPermissions(...permissions);
  }

  public checkPermissionsDetailed(
    ...requiredPermissions: any[]
  ): IPermissionCheckResult[] {
    return PermissionService.checkPermissionsDetailed(
      this.getUserPermissionsSync(),
      ...requiredPermissions,
    );
  }

  /**
   * Check if user has app-level permissions
   */
  public hasAppLevelPermissions(): boolean {
    return this.hasPermissions(...APP_LEVEL_PERMISSIONS);
  }

  /**
   * Check if user has DM preferences permission
   */
  public hasDMPreferencesPermission(): boolean {
    return this.hasPermissions({
      app_code: SupportedAppCodes.XDM,
      type: PermissionType.action,
      name: 'preferences.View',
    });
  }

  private getUserPermissionsSync(): any[] {
    return this.stateService.getByPath(
      ['userSession', 'userPermissions', 'items'],
      [],
    );
  }

  private getUserPortalPermissionsSync(): any[] {
    return filter(this.getUserPermissionsSync(), {
      app_code: SupportedAppCodes.XPRT,
    });
  }

  private getUserXDMPermissionsSync(): any[] {
    return filter(this.getUserPermissionsSync(), {
      app_code: SupportedAppCodes.XDM,
    });
  }
}
